'use client'

import { type ReactNode } from 'react'
import { cn } from '../lib/cn'

export interface ContentProps {
  /** Unique section identifier for tracking */
  id: string
  /** Human-readable label shown in analytics */
  label?: string
  /** Section headline */
  headline: string
  /** Optional subline above headline */
  subline?: string
  /** Optional description text */
  description?: string
  /** Theme variant */
  theme?: 'dark' | 'light'
  /** Additional className */
  className?: string
  /** Additional content (children) */
  children?: ReactNode
}

/**
 * Content Section Component
 *
 * General-purpose content section for presentations.
 * Automatically tracked with the provided id.
 *
 * @example
 * ```tsx
 * <Content
 *   id="challenge"
 *   label="The Challenge"
 *   headline="Current Market Situation"
 *   description="An overview of the challenges facing the industry..."
 * />
 * ```
 */
export function Content({
  id,
  label,
  headline,
  subline,
  description,
  theme = 'dark',
  className,
  children,
}: ContentProps) {
  const isDark = theme === 'dark'

  return (
    <section
      data-section-id={id}
      data-section-label={label || headline}
      className={cn(
        'min-h-screen flex items-center py-24 px-8 md:px-20',
        isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900',
        className
      )}
    >
      <div className="w-full max-w-4xl mx-auto text-center">
        {/* Subline */}
        {subline && (
          <p
            className={cn(
              'text-xs md:text-sm tracking-[0.2em] uppercase mb-4',
              isDark ? 'text-white/60' : 'text-gray-900/60'
            )}
          >
            {subline}
          </p>
        )}

        {/* Headline */}
        <h2
          className={cn(
            'text-3xl md:text-5xl font-bold mb-6 whitespace-pre-line',
            isDark ? 'text-white' : 'text-gray-900'
          )}
        >
          {headline}
        </h2>

        {/* Description */}
        {description && (
          <p
            className={cn(
              'text-lg md:text-xl leading-relaxed max-w-3xl mx-auto',
              isDark ? 'text-white/80' : 'text-gray-900/80'
            )}
          >
            {description}
          </p>
        )}

        {/* Children */}
        {children && <div className="mt-12">{children}</div>}
      </div>
    </section>
  )
}
